
import { ProjectRegistryData } from '@wren/shared/src/lib/isometric';
import { useState } from 'react';
import { articleStyles, container } from '../../../style/article';
import { alignCenter, flexText, genericFlex } from '../../../style/flex';
import { colors, fonts } from '../../../style/theme';
import { scale } from '../../../util/scale';
import Button from '../../buttons/Button';
import Cloud from '../../svgs/Cloud';
import Hourglass from '../../svgs/Hourglass';
import ReceiptCheck from '../../svgs/ReceiptCheck';
import ExpandableContent from '../../UI/ExpandableContent';
import Table from '../../UI/Table';

const rowSubtext = scale({
  margin: '0 !important',
  color: colors.textSecondary,
  fontWeight: 800,
});

const pill = scale({
  fontFamily: fonts.Sans,
  borderRadius: 21,
  backgroundColor: 'var(--success-green-low-opacity)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'var(--wren-green)',
  fontSize: 16,
  fontWeight: 600,
  padding: '0px 10px',
  lineHeight: '26px',
});

const status = scale({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginRight: 8,
  width: 24,
});

const orderTopper = scale({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  height: 60,
  padding: '0 18px',
  marginTop: 18,
  border: `1px solid var(--border-color)`,
  backgroundColor: 'var(--background-gray)',
  borderTopRightRadius: 12,
  borderTopLeftRadius: 12,
});

const expandableHeader = scale({
  fontFamily: 'Blanco, Georgia, serif',
  fontSize: '20px',
  lineHeight: 0,
  color: 'var(--text-primary)',
});

const fadeButtonContainer = scale({
  zIndex: 2,
  overflow: 'hidden',
  height: 'calc(80% + 8px)',
  width: 'calc(100% + 8px)', // add a bit extra width
  left: -4, // extra width accomodates box shadow on white card style
  bottom: -8,
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'center',
});

const fadeStyle = (rgb: string) =>
  scale({
    position: 'absolute',
    background: [
      `linear-gradient(rgba(${rgb},0) 0%, rgba(${rgb},1) 70%, rgba(${rgb},1) 100%)`,
      `linear-gradient(rgba(${rgb},0) 0%, rgba(${rgb},1) 60%, rgba(${rgb},1) 100%)`,
    ],
  });

const showMoreButtonContainer = scale({
  width: 200,
  margin: 'auto',
  marginTop: 130,
});
const showLessButtonContainer = scale({
  width: 200,
  margin: 'auto',
  marginBottom: 43,
  marginTop: 36,
});

export default function CreditTimeline({
  registryData,
}: {
  registryData: ProjectRegistryData;
}) {
  const { creditTimeline } = registryData || {};
  if (!creditTimeline?.length) {
    return null;
  }

  const [shownCreditEntries, setShownCreditEntries] = useState(
    creditTimeline.length > 2 ? creditTimeline.slice(0, 2) : creditTimeline
  );

  const formatDate = (dateString: string): string => {
    return new Intl.DateTimeFormat('en-US').format(new Date(dateString));
  };

  const iconFromStatus = (status: string) => {
    switch (status) {
      case 'placed':
        return <ReceiptCheck stroke={colors.textSecondary} size={18} />;
      case 'in_progress':
        return <Hourglass stroke={colors.textSecondary} size={18} />;
      default:
        return <Cloud />;
    }
  };

  const hasMoreCreditEntriesToShow =
    shownCreditEntries.length < creditTimeline.length;

  return (
    <div css={{ width: '100%' }}>
      <div css={[articleStyles]}>
        <h3 id="credit-timeline">Credit timeline</h3>
        <p>
          This timeline is provided by{' '}
          <a href={registryData.url} target="_blank" rel="noopener noreferrer">
            {registryData.registry}
          </a>
          , the registry that helps ensure the accuracy and reliability of this
          project. It does not necessarily reflect all credit orders if any were
          purchased outside of this registry.
        </p>
      </div>
      <div css={container}>
        <div
          css={
            hasMoreCreditEntriesToShow
              ? { position: 'relative', marginBottom: 56 }
              : {}
          }
        >
          {shownCreditEntries.map((entry) => (
            <div key={entry.id}>
              <div css={orderTopper}>
                <div css={[genericFlex, alignCenter]}>
                  <div title={`status: ${entry.status}`} css={status}>
                    {iconFromStatus(entry.status)}
                  </div>
                  <p css={rowSubtext} title={entry.id}>
                    Ordered {formatDate(entry.effective_contract_date)}
                  </p>
                </div>
                <div css={pill}>
                  {entry.credits_ordered.credits} tonnes CO₂e
                </div>
              </div>
              {entry.deliveries?.length ? (
                entry.deliveries.map((delivery) => (
                  <ExpandableContent
                    key={delivery.delivery.id}
                    header={
                      <span css={flexText}>
                        <span css={pill}>
                          {delivery.delivery.credit_batch_size_total?.credits ||
                            0}{' '}
                          tonnes CO₂e
                        </span>
                        <span css={expandableHeader}>
                          {' '}
                          delivered at{' '}
                          <a
                            href={delivery.delivery.url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {formatDate(delivery.delivery.delivered_at)}
                          </a>
                        </span>
                      </span>
                    }
                  >
                    <Table
                      css={scale({
                        margin: '0 !important',
                      })}
                      headings={[
                        'Sequestered on',
                        'Issued at',
                        'Retired at',
                        'Net carbon removed',
                      ]}
                      rows={delivery.creditBatches.map(
                        (batch: {
                          sequestered_on: string;
                          issued_at: string;
                          retired_at: string;
                          carbon_removal_data: {
                            net_carbon_removed_kg: number;
                          };
                          url: string;
                        }) => [
                          formatDate(batch.sequestered_on),
                          formatDate(batch.issued_at),
                          <a
                            href={batch.url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {formatDate(batch.retired_at)}
                          </a>,
                          <span css={pill}>
                            {batch.carbon_removal_data.net_carbon_removed_kg /
                              1000}{' '}
                            tonnes CO₂e
                          </span>,
                        ]
                      )}
                    ></Table>
                  </ExpandableContent>
                ))
              ) : (
                <ExpandableContent
                  header={
                    <span css={flexText}>
                      <span css={pill}>0 tonnes CO₂e</span>{' '}
                      <span css={expandableHeader}> delivered</span>
                    </span>
                  }
                >
                  <p>
                    When credits are delivered, they will be displayed here.
                  </p>
                </ExpandableContent>
              )}
            </div>
          ))}
          {creditTimeline.length > 2 && (
            <div
              css={
                hasMoreCreditEntriesToShow
                  ? [fadeStyle('255, 255, 255'), fadeButtonContainer]
                  : fadeButtonContainer
              }
            >
              <div
                css={
                  hasMoreCreditEntriesToShow
                    ? showMoreButtonContainer
                    : showLessButtonContainer
                }
              >
                <Button
                  size="medium"
                  type="grayUppercase"
                  text={hasMoreCreditEntriesToShow ? 'Show more' : 'Show less'}
                  onClickFn={() => {
                    if (hasMoreCreditEntriesToShow) {
                      setShownCreditEntries(creditTimeline);
                    } else {
                      setShownCreditEntries(creditTimeline.slice(0, 2));
                    }
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
